import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../AppIcon';
import Button from './Button';
import { stripeService } from '../../services/stripeService';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { signOut, userProfile } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const navigationItems = [
    { label: 'Dashboard', path: '/agent-dashboard', icon: 'LayoutDashboard' },
    { label: 'Account', path: '/account?tab=subscription', icon: 'User' },
    { label: 'About', path: '/about-offer-sense-ai-logged-in', icon: 'Info' },
  ];

  // Add admin dashboard link if user is admin
  if (userProfile?.is_admin) {
    navigationItems?.push({ label: 'Admin', path: '/admin-dashboard', icon: 'ShieldAlert' });
  }

  const isActive = (path) => location?.pathname === path;
  
  // Check if we're on the registration page
  const isRegistrationPage = location?.pathname === '/user-registration';

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      const { error } = await signOut();
      if (error) {
        console.error('Logout error:', error);
        alert('Failed to logout. Please try again.');
      } else {
        navigate('/user-login');
      }
    } catch (error) {
      console.error('Logout error:', error);
      alert('Failed to logout. Please try again.');
    } finally {
      setIsLoggingOut(false);
      setMobileMenuOpen(false);
    }
  };

  return (
    <>
      <header className="header">
        <div className="header-container">
          <a href="/agent-dashboard" className="header-logo" onClick={(e) => { e?.preventDefault(); navigate('/agent-dashboard'); }}>
            <div className="header-logo-icon">
              <img src="/assets/images/90A19BF4-3446-4543-87E4-2148054C81C1-1768952058269.jpeg" alt="OfferSense Logo" className="w-10 h-10 object-contain" />
            </div>
            <span className="header-logo-text">
              <span className="text-[#2C3E5F] font-bold">OfferSense</span>
              <span className="text-[#5DBFB3] font-bold">.Ai</span>
            </span>
          </a>

          <nav className="header-nav">
            {navigationItems?.map((item) => (
              <button
                key={item?.path}
                onClick={() => handleNavigation(item?.path)}
                className={`header-nav-item ${isActive(item?.path) ? 'active' : ''}`}
              >
                <Icon name={item?.icon} size={20} className="mr-2" />
                {item?.label}
              </button>
            ))}
          </nav>

          <div className="header-actions">
            {!isRegistrationPage && <SubscriptionBadge tier={userProfile?.subscription_tier || 'free'} />}
            
            {!isRegistrationPage && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                disabled={isLoggingOut}
                iconName="LogOut"
                iconPosition="left"
                className="hidden sm:flex"
              >
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </Button>
            )}
            
            <button
              onClick={toggleMobileMenu}
              className="header-mobile-toggle"
              aria-label="Toggle mobile menu"
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      <div className={`header-mobile-menu ${mobileMenuOpen ? 'open' : ''}`}>
        <div className="header-mobile-header">
          <a href="/agent-dashboard" className="header-logo" onClick={(e) => { e?.preventDefault(); navigate('/agent-dashboard'); setMobileMenuOpen(false); }}>
            <div className="header-logo-icon">
              <img src="/assets/images/90A19BF4-3446-4543-87E4-2148054C81C1-1768952058269.jpeg" alt="OfferSense Logo" className="w-10 h-10 object-contain" />
            </div>
            <span className="header-logo-text">
              <span className="text-[#2C3E5F] font-bold">OfferSense</span>
              <span className="text-[#5DBFB3] font-bold">.Ai</span>
            </span>
          </a>
          <button
            onClick={toggleMobileMenu}
            className="p-2 rounded-lg hover:bg-muted transition-colors"
            aria-label="Close mobile menu"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <nav className="header-mobile-nav">
          {navigationItems?.map((item) => (
            <button
              key={item?.path}
              onClick={() => handleNavigation(item?.path)}
              className={`header-mobile-nav-item ${isActive(item?.path) ? 'active' : ''}`}
            >
              <Icon name={item?.icon} size={20} className="mr-3" />
              {item?.label}
            </button>
          ))}
          
          {!isRegistrationPage && (
            <div className="mt-6 pt-6 border-t border-border">
              <SubscriptionBadge tier={userProfile?.subscription_tier || 'free'} showUpgrade />
              
              <Button
                variant="outline"
                size="md"
                onClick={handleLogout}
                disabled={isLoggingOut}
                iconName="LogOut"
                iconPosition="left"
                className="w-full mt-4"
              >
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </Button>
            </div>
          )}
        </nav>
      </div>
    </>
  );
};

const SubscriptionBadge = ({ tier = 'free', showUpgrade = false }) => {
  const navigate = useNavigate();
  const { userProfile } = useAuth();
  const isPilotMode = stripeService?.isPilotMode(userProfile);

  const tierConfig = {
    free: {
      label: 'Free Plan',
      icon: 'Zap',
      className: 'free',
    },
    pro: {
      label: isPilotMode ? 'Pilot Plan' : 'Pro Plan',
      icon: isPilotMode ? 'Rocket' : 'Crown',
      className: 'pro',
    },
    enterprise: {
      label: 'Enterprise',
      icon: 'Building',
      className: 'enterprise',
    },
  };

  const config = tierConfig?.[tier] || tierConfig?.free;

  const handleUpgrade = () => {
    navigate('/account?tab=subscription');
  };

  return (
    <div className="flex items-center gap-2">
      <div className={`subscription-badge ${config?.className}`}>
        <Icon name={config?.icon} size={16} />
        <span className="hidden sm:inline">{config?.label}</span>
      </div>
      {showUpgrade && tier === 'free' && (
        <Button
          variant="outline"
          size="sm"
          onClick={handleUpgrade}
          iconName="ArrowUpRight"
          iconPosition="right"
        >
          Upgrade
        </Button>
      )}
    </div>
  );
};

export default Header;